package dev.wyns.ventrova

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import dev.wyns.ventrova.data.sqlite.SqlJsDatabaseOpener
import dev.wyns.ventrova.data.sqlite.SqlitePlaceRepository
import dev.wyns.ventrova.platform.BrowserBackNavigation
import dev.wyns.ventrova.platform.LocalSuggestionService
import dev.wyns.ventrova.platform.WebExternalActions
import dev.wyns.ventrova.platform.WebKeyValueStore
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.app_name
import dev.wyns.ventrova.resources.bricolage_grotesque_bold
import dev.wyns.ventrova.resources.bricolage_grotesque_extrabold
import dev.wyns.ventrova.resources.geist_medium
import dev.wyns.ventrova.resources.geist_regular
import dev.wyns.ventrova.resources.geist_semibold
import dev.wyns.ventrova.resources.inter_medium
import dev.wyns.ventrova.resources.inter_regular
import dev.wyns.ventrova.resources.inter_semibold
import dev.wyns.ventrova.resources.roboto_medium
import dev.wyns.ventrova.resources.roboto_regular
import dev.wyns.ventrova.resources.roboto_semibold
import dev.wyns.ventrova.ui.android.AndroidApp
import dev.wyns.ventrova.ui.web.WebApp
import dev.wyns.ventrova.ui.web.WebLayout
import dev.wyns.ventrova.ui.web.cupertino.WebCupertinoApp
import dev.wyns.ventrova.ui.web.detectWebLayout
import dev.wyns.ventrova.ui.web.robotoFontFamily
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.FontResource
import org.jetbrains.compose.resources.getString
import org.jetbrains.compose.resources.preloadFont
import org.jetbrains.skiko.wasm.onWasmReady
import org.maplibre.compose.browser.installMapLibreCompose

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    // Reachable use so production dead code elimination keeps the zone rules.
    @Suppress("UNUSED_VARIABLE") val keepZoneRules = jsJodaTimeZone

    val dependencies = AppDependencies(
        placeRepository = SqlitePlaceRepository(SqlJsDatabaseOpener()),
        keyValueStore = WebKeyValueStore(),
        externalActions = WebExternalActions(),
        suggestionService = LocalSuggestionService(),
    )
    onWasmReady {
        // Must run before Compose builds its renderer, which creates the GPU context the map composites into.
        installMapLibreCompose()
        val layout = detectWebLayout()
        val backNavigation = BrowserBackNavigation()
        // A dedicated container: Compose replaces the container's content, the splash lives next to it.
        ComposeViewport(document.getElementById("app")!!) {
            HideSplashWhenReady(layout.fonts())
            when (layout) {
                WebLayout.Desktop -> WebApp(dependencies)
                WebLayout.Android -> AndroidApp(dependencies, backNavigation, bodyFont = { robotoFontFamily() })
                WebLayout.Ios -> WebCupertinoApp(dependencies, backNavigation)
            }
        }
    }
}

/**
 * Fades out the HTML splash screen (index.html) once the layout's [fonts] and the strings have
 * loaded (both load asynchronously on the web) and the first frames are drawn. Hides it after a
 * while regardless, so a failing font never blocks the app.
 */
@OptIn(ExperimentalResourceApi::class)
@Composable
private fun HideSplashWhenReady(fonts: List<FontResource>) {
    val loaded = fonts.map { preloadFont(it) }
    val fontsReady = loaded.all { it.value != null }
    LaunchedEffect(fontsReady) {
        if (!fontsReady) return@LaunchedEffect
        getString(Res.string.app_name)
        withFrameNanos { }
        withFrameNanos { }
        hideSplash()
    }
    LaunchedEffect(Unit) {
        delay(20_000)
        hideSplash()
    }
}

private fun hideSplash() {
    val hide: dynamic = window.asDynamic().ventrovaHideSplash
    if (hide != undefined) hide()
}

private val DisplayFonts = listOf(Res.font.bricolage_grotesque_bold, Res.font.bricolage_grotesque_extrabold)

/** Fonts each layout needs before it looks right. */
private fun WebLayout.fonts(): List<FontResource> = DisplayFonts + when (this) {
    WebLayout.Desktop -> listOf(Res.font.geist_regular, Res.font.geist_medium, Res.font.geist_semibold)
    WebLayout.Android -> listOf(Res.font.roboto_regular, Res.font.roboto_medium, Res.font.roboto_semibold)
    WebLayout.Ios -> listOf(Res.font.inter_regular, Res.font.inter_medium, Res.font.inter_semibold)
}
